<template>
  <div class="modal fade" :id="`details-${applicationId}`" tabindex="-1" role="dialog"
       :aria-labelledby="`details-${applicationId}-label`" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title" :id="`details-${applicationId}-label`">
            <i class="bx bx-file me-2"></i>
            Application: {{ application.application_number || 'Loading...' }}
          </h5>
          <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <!-- Loading state -->
        <div class="modal-body" v-if="loading">
          <div class="text-center p-4">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading application details...</p>
          </div>
        </div>

        <!-- Content when loaded -->
        <div class="modal-body" v-else>
          <!-- Status summary card -->
          <div class="status-summary mb-4">
            <div class="card">
              <div class="card-body">
                <div class="row align-items-center">
                  <div class="col-md-8">
                    <h4 class="mb-1">{{ application.project_title }}</h4>
                    <p class="text-muted mb-0">
                      {{ capitalizeFirstLetter(application.development_type) }} |
                      Plot #{{ application.plot_number }}
                    </p>
                  </div>
                  <div class="col-md-4 text-md-end">
                    <span :class="['status-badge', getStatusClass(application.status)]">
                      {{ capitalizeFirstLetter(application.status) }}
                    </span>
                    <div class="mt-2">
                      <span :class="['payment-badge', getPaymentStatusClass(application.paymentStatus)]">
                        {{ application.paymentStatus }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabbed sections for better organization -->
          <ul class="nav nav-tabs" id="detailsTabs" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" id="project-tab" data-bs-toggle="tab" href="#project" role="tab"
                 aria-controls="project" aria-selected="true">
                <i class="bx bx-building me-1"></i> Project
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" id="owner-tab" data-bs-toggle="tab" href="#owner" role="tab"
                 aria-controls="owner" aria-selected="false">
                <i class="bx bx-user me-1"></i> Owner
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" id="payment-tab" data-bs-toggle="tab" href="#payment" role="tab"
                 aria-controls="payment" aria-selected="false">
                <i class="bx bx-money me-1"></i> Payment
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" id="documents-tab" data-bs-toggle="tab" href="#documents" role="tab"
                 aria-controls="documents" aria-selected="false">
                <i class="bx bx-file me-1"></i> Documents
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" id="workflow-tab" data-bs-toggle="tab" href="#workflow" role="tab"
                 aria-controls="workflow" aria-selected="false">
                <i class="bx bx-transfer me-1"></i> Workflow
              </a>
            </li>
            <li class="nav-item" v-if="application.status === 'pending'">
              <a class="nav-link" id="billing-tab" data-bs-toggle="tab" href="#billing" role="tab"
                 aria-controls="billing" aria-selected="false">
                <i class="bx bx-receipt me-1"></i> Billing
              </a>
            </li>
          </ul>

          <div class="tab-content p-3" id="detailsTabContent">
            <!-- Project Details Tab -->
            <div class="tab-pane fade show active" id="project" role="tabpanel" aria-labelledby="project-tab">
              <div class="row">
                <!-- Project Info -->
                <div class="col-md-6">
                  <div class="info-card">
                    <h6 class="section-title"><i class="bx bx-info-circle me-2"></i>Project Details</h6>
                    <table class="table table-sm details-table">
                      <tbody>
                      <tr>
                        <th width="40%">Project Title</th>
                        <td>{{ application.project_title }}</td>
                      </tr>
                      <tr>
                        <th>Development Type</th>
                        <td>{{ capitalizeFirstLetter(application.development_type) }}</td>
                      </tr>
                      <tr>
                        <th>Project Value</th>
                        <td>KES {{ formatCurrency(application.project_value) }}</td>
                      </tr>
                      <tr>
                        <th>Description</th>
                        <td>{{ application.project_description }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Plot Info -->
                <div class="col-md-6">
                  <div class="info-card">
                    <h6 class="section-title"><i class="bx bx-map-alt me-2"></i>Plot Details</h6>
                    <table class="table table-sm details-table">
                      <tbody>
                      <tr>
                        <th width="40%">Plot Number</th>
                        <td>{{ application.plot_number }}</td>
                      </tr>
                      <tr>
                        <th>UPN Number</th>
                        <td>{{ application.upn_number || 'N/A' }}</td>
                      </tr>
                      <tr>
                        <th>Plot Size</th>
                        <td>{{ application.plot_size }} sq. meters</td>
                      </tr>
                      <tr>
                        <th>Land Type</th>
                        <td>{{ application.land_type }}</td>
                      </tr>
                      <tr>
                        <th>Land Use</th>
                        <td>{{ application.land_use }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Professional Info -->
              <div class="info-card mt-4">
                <h6 class="section-title"><i class="bx bx-user-pin me-2"></i>Professional Details</h6>
                <table class="table table-sm details-table">
                  <tbody>
                  <tr>
                    <th width="20%">Name</th>
                    <td>{{ application.professional_name }}</td>
                  </tr>
                  <tr>
                    <th>Type</th>
                    <td>{{ formatProfessionalType(application.professional_type) }}</td>
                  </tr>
                  <tr>
                    <th>Certificate Number</th>
                    <td>{{ application.certificate_number }}</td>
                  </tr>
                  <tr>
                    <th>Contact</th>
                    <td>
                      <i class="bx bx-phone me-1"></i> {{ application.professional_phone }}<br>
                      <i class="bx bx-envelope me-1"></i> {{ application.professional_email }}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Owner Details Tab -->
            <div class="tab-pane fade" id="owner" role="tabpanel" aria-labelledby="owner-tab">
              <div class="info-card">
                <h6 class="section-title"><i class="bx bx-user me-2"></i>Owner Information</h6>
                <table class="table table-sm details-table">
                  <tbody>
                  <tr>
                    <th width="30%">Full Name</th>
                    <td>{{ application.owner_full_names }}</td>
                  </tr>
                  <tr>
                    <th>ID Number</th>
                    <td>{{ application.owner_id_number || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <th>KRA PIN</th>
                    <td>{{ application.owner_kra_pin || 'N/A' }}</td>
                  </tr>
                  <tr>
                    <th>Contact</th>
                    <td>
                      <i class="bx bx-phone me-1"></i> {{ application.owner_phone_number }}<br>
                      <i class="bx bx-envelope me-1"></i> {{ application.owner_email || 'N/A' }}
                    </td>
                  </tr>
                  <tr>
                    <th>Address</th>
                    <td>{{ application.owner_address }}</td>
                  </tr>
                  <tr>
                    <th>Location</th>
                    <td>{{ getSubCountyName() }} County, {{ getWardName() }} Ward</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="info-card mt-4">
                <h6 class="section-title"><i class="bx bx-receipt me-2"></i>Submission Info</h6>
                <table class="table table-sm details-table">
                  <tbody>
                  <tr>
                    <th width="30%">Submitted By</th>
                    <td>{{ application.created_by }}</td>
                  </tr>
                  <tr>
                    <th>ID Number</th>
                    <td>{{ application.created_by_id }}</td>
                  </tr>
                  <tr>
                    <th>Date Submitted</th>
                    <td>{{ formatDateTime(application.created_at) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Payment Details Tab -->
            <div class="tab-pane fade" id="payment" role="tabpanel" aria-labelledby="payment-tab">
              <!-- Initial Payment -->
              <div class="info-card mb-4">
                <h6 class="section-title">
                  <i class="bx bx-credit-card me-2"></i>Initial Payment Details
                  <span :class="['float-end', 'badge', getPaymentStatusClass(application.paymentStatus)]">
                    {{ application.paymentStatus }}
                  </span>
                </h6>
                <table class="table table-sm details-table">
                  <tbody>
                  <tr>
                    <th width="30%">Bill Number</th>
                    <td>{{ application.billNo }}</td>
                  </tr>
                  <tr>
                    <th>Bill Amount</th>
                    <td>KES {{ formatCurrency(application.detailAmount) }}</td>
                  </tr>
                  <tr>
                    <th>Amount Paid</th>
                    <td>KES {{ formatCurrency(application.receiptAmount) }}</td>
                  </tr>
                  <tr>
                    <th>Balance</th>
                    <td>
                        <span :class="{'text-danger': parseFloat(application.billBalance) > 0}">
                          KES {{ formatCurrency(application.billBalance) }}
                        </span>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <!-- Initial Fee Breakdown -->
              <div class="card mb-4">
                <div class="card-header bg-light">
                  <h6 class="mb-0"><i class="bx bx-list-ul me-2"></i>Initial Fee Breakdown</h6>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive">
                    <table class="table table-sm table-hover mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Fee Description</th>
                        <th class="text-end">Amount (KES)</th>
                        <th class="text-end">Paid</th>
                        <th class="text-end">Balance</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="fee in fees" :key="fee.id">
                        <td>{{ fee.feeDescription }}</td>
                        <td class="text-end">{{ formatCurrency(fee.detailAmount) }}</td>
                        <td class="text-end">{{ formatCurrency(fee.receiptAmount) }}</td>
                        <td class="text-end" :class="{'text-danger': parseFloat(fee.billBalance) > 0}">
                          {{ formatCurrency(fee.billBalance) }}
                        </td>
                      </tr>
                      <tr v-if="fees.length === 0">
                        <td colspan="4" class="text-center py-3">No fee details available</td>
                      </tr>
                      </tbody>
                      <tfoot class="table-light">
                      <tr>
                        <th>Total</th>
                        <th class="text-end">{{ getTotalAmount() }}</th>
                        <th class="text-end">{{ getTotalPaid() }}</th>
                        <th class="text-end" :class="{'text-danger': parseFloat(getTotalBalance()) > 0}">
                          {{ getTotalBalance() }}
                        </th>
                      </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
                <div class="card-footer bg-white text-end">
                  <button type="button" class="btn btn-sm btn-primary" @click="printBill(application.billNo)">
                    <i class="bx bx-download me-1"></i> Download Invoice
                  </button>
                  <button v-if="application.paymentStatus === 'PAID'" type="button" class="btn btn-sm btn-info ms-2"
                          @click="downloadReceipt">
                    <i class="bx bx-receipt me-1"></i> Download Receipt
                  </button>
                </div>
              </div>

              <!-- Final Payment Details (if applicable) -->
              <div v-if="finalFees && finalFees.length > 0" class="mt-4">
                <div class="info-card mb-4">
                  <h6 class="section-title">
                    <i class="bx bx-credit-card me-2"></i>Final Payment Details
                    <span :class="['float-end', 'badge', getPaymentStatusClass(application.paymentStatus)]">
                      {{ application.paymentStatus }}
                    </span>
                  </h6>
                  <table class="table table-sm details-table">
                    <tbody>
                    <tr>
                      <th width="30%">Bill Number</th>
                      <td>{{ application.billNoFinal }}</td>
                    </tr>
                    <tr>
                      <th>Bill Amount</th>
                      <td>KES {{ formatCurrency(application.detailAmountFinal) }}</td>
                    </tr>
                    <tr>
                      <th>Amount Paid</th>
                      <td>KES {{ formatCurrency(application.receiptAmountFinal) }}</td>
                    </tr>
                    <tr>
                      <th>Balance</th>
                      <td>
                          <span :class="{'text-danger': parseFloat(application.billBalanceFinal) > 0}">
                            KES {{ formatCurrency(application.billBalanceFinal) }}
                          </span>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Final Fee Breakdown -->
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0"><i class="bx bx-list-ul me-2"></i>Final Fee Breakdown</h6>
                  </div>
                  <div class="card-body p-0">
                    <div class="table-responsive">
                      <table class="table table-sm table-hover mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Fee Description</th>
                          <th class="text-end">Amount (KES)</th>
                          <th class="text-end">Paid</th>
                          <th class="text-end">Balance</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="fee in finalFees" :key="fee.id">
                          <td>{{ fee.feeDescription }}</td>
                          <td class="text-end">{{ formatCurrency(fee.detailAmount) }}</td>
                          <td class="text-end">{{ formatCurrency(fee.receiptAmount) }}</td>
                          <td class="text-end" :class="{'text-danger': parseFloat(fee.billBalance) > 0}">
                            {{ formatCurrency(fee.billBalance) }}
                          </td>
                        </tr>
                        </tbody>
                        <tfoot class="table-light">
                        <tr>
                          <th>Total</th>
                          <th class="text-end">{{ getFinalTotalAmount() }}</th>
                          <th class="text-end">{{ getFinalTotalPaid() }}</th>
                          <th class="text-end" :class="{'text-danger': parseFloat(getFinalTotalBalance()) > 0}">
                            {{ getFinalTotalBalance() }}
                          </th>
                        </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Documents Tab -->
            <div class="tab-pane fade" id="documents" role="tabpanel" aria-labelledby="documents-tab">
              <div class="card">
                <div class="card-header bg-light">
                  <h6 class="mb-0"><i class="bx bx-folder-open me-2"></i>Uploaded Documents</h6>
                </div>
                <div class="card-body">
                  <div v-if="documents && documents.length > 0" class="document-list">
                    <div v-for="doc in documents" :key="doc.id" class="document-item">
                      <div class="row align-items-center">
                        <div class="col-md-1 text-center">
                          <i class="bx bx-file text-primary fs-4"></i>
                        </div>
                        <div class="col-md-9">
                          <h6 class="mb-0">{{ formatDocumentType(doc.document_type) }}</h6>
                          <small class="text-muted">{{ doc.document_name }}</small>
                          <div v-if="doc.expiry_date" class="badge bg-warning text-dark mt-1">
                            Expires: {{ formatDate(doc.expiry_date) }}
                          </div>
                        </div>
                        <div class="col-md-2 text-end">
                          <a :href="doc.file_path" target="_blank" class="btn btn-sm btn-outline-primary">
                            <i class="bx bx-download me-1"></i> View
                          </a>
                        </div>
                      </div>
                      <hr class="my-2">
                    </div>
                  </div>
                  <div v-else class="text-center py-4">
                    <i class="bx bx-info-circle text-muted fs-1"></i>
                    <p class="text-muted mt-2">No documents have been uploaded for this application.</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Workflow Tab -->
            <div class="tab-pane fade" id="workflow" role="tabpanel" aria-labelledby="workflow-tab">
              <div class="card">
                <div class="card-header bg-light">
                  <h6 class="mb-0"><i class="bx bx-git-branch me-2"></i>Application Workflow</h6>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive">
                    <table class="table table-hover mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Stage</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Comments</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="step in workflow" :key="step.id">
                        <td>
                          <span class="workflow-stage">{{ formatWorkflowStage(step.stage) }}</span>
                        </td>
                        <td>
                            <span :class="['status-pill', getWorkflowStatusClass(step.status)]">
                              {{ capitalizeFirstLetter(step.status) }}
                            </span>
                        </td>
                        <td>{{ formatDateTime(step.action_date || step.created_at) }}</td>
                        <td>
                          <span v-if="step.comments">{{ step.comments }}</span>
                          <span v-else class="text-muted">No comments</span>
                        </td>
                      </tr>
                      <tr v-if="workflow.length === 0">
                        <td colspan="4" class="text-center py-3">No workflow data available</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Billing Tab (only for pending applications) -->
            <div class="tab-pane fade" id="billing" role="tabpanel" aria-labelledby="billing-tab"
                 v-if="application.status === 'pending'">
              <div class="row">
                <!-- Left side - Enter bill details -->
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0"><i class="bx bx-plus-circle me-2"></i>Add New Bill Item</h6>
                    </div>
                    <div class="card-body">
                      <!-- Income Type Selection -->
                      <div class="mb-3">
                        <label for="incomeType" class="form-label">Income Type</label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="bx bx-category"></i></span>
                          <select
                              class="form-select"
                              id="incomeType"
                              v-model="selectedIncomeType"
                              :disabled="loadingIncomeTypes"
                          >
                            <option value="">Select Income Type</option>
                            <option v-if="loadingIncomeTypes" value="" disabled>Loading...</option>
                            <option
                                v-for="type in incomeTypes"
                                :key="type.incomeTypeId"
                                :value="type.incomeTypeId"
                            >
                              {{ type.incomeTypeDescription }}
                            </option>
                          </select>
                        </div>
                        <div v-if="loadingIncomeTypes" class="text-muted small mt-1">
                          <i class="bx bx-loader bx-spin"></i> Loading income types...
                        </div>
                      </div>

                      <!-- Fees and Charges Selection -->
                      <div class="mb-3">
                        <label for="feeAndCharge" class="form-label">Account Name</label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="bx bx-list-ul"></i></span>
                          <select
                              class="form-select"
                              id="feeAndCharge"
                              v-model="selectedFeeAndCharge"
                              :disabled="!selectedIncomeType || loadingFeesAndCharges"
                          >
                            <option value="">Select Fee/Charge</option>
                            <option v-if="loadingFeesAndCharges" value="" disabled>Loading...</option>
                            <option
                                v-for="fee in feesAndCharges"
                                :key="fee.feeId"
                                :value="fee.feeId"
                            >
                              {{ fee.feeDescription }} ({{ fee.currency }} {{ fee.unitFeeAmount }})
                            </option>
                          </select>
                        </div>
                        <div v-if="loadingFeesAndCharges" class="text-muted small mt-1">
                          <i class="bx bx-loader bx-spin"></i> Loading fees and charges...
                        </div>
                        <div v-if="!selectedIncomeType && !loadingIncomeTypes" class="text-muted small mt-1">
                          <i class="bx bx-info-circle"></i> Please select an Income Type first
                        </div>
                      </div>

                      <!-- Details -->
                      <div class="mb-3">
                        <label for="details" class="form-label">Details</label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="bx bx-info-circle"></i></span>
                          <textarea
                              id="details"
                              class="form-control"
                              rows="3"
                              v-model="billingForm.details"
                              placeholder="Description of the charge"
                          ></textarea>
                        </div>
                      </div>

                      <div class="row">
                        <!-- Quantity -->
                        <div class="col-md-6 mb-3">
                          <label for="quantity" class="form-label">Quantity</label>
                          <div class="input-group">
                            <span class="input-group-text"><i class="bx bx-hash"></i></span>
                            <input
                                type="number"
                                id="quantity"
                                class="form-control"
                                v-model="billingForm.quantity"
                                min="1"
                            >
                          </div>
                        </div>

                        <!-- Amount -->
                        <div class="col-md-6 mb-3">
                          <label for="amount" class="form-label">Amount</label>
                          <div class="input-group">
                            <span class="input-group-text">KES</span>
                            <input
                                type="number"
                                id="amount"
                                class="form-control"
                                v-model="billingForm.amount"
                                readonly
                            >
                          </div>
                          <small class="text-muted">
                            <i class="bx bx-info-circle"></i> Calculated based on unit amount × quantity
                          </small>
                        </div>
                      </div>

                      <!-- Add Button -->
                      <button
                          class="btn btn-primary w-100"
                          @click="addBillItem"
                          :disabled="!selectedIncomeType || !selectedFeeAndCharge"
                      >
                        <i class="bx bx-plus me-1"></i> Add to Bill
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Right side - Created Bill Details -->
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">
                        <i class="bx bx-receipt me-2"></i>Bill Summary
                        <span class="badge bg-primary float-end">{{ billItems.length }} items</span>
                      </h6>
                    </div>
                    <div class="card-body p-0">
                      <div class="table-responsive">
                        <table class="table table-hover mb-0">
                          <thead class="table-light">
                          <tr>
                            <th>#</th>
                            <th>Details</th>
                            <th class="text-end">Amount</th>
                            <th class="text-center">Actions</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-if="billItems.length === 0">
                            <td colspan="4" class="text-center py-4">
                              <i class="bx bx-basket text-muted fs-3"></i>
                              <p class="text-muted mt-2">No items added to bill yet</p>
                            </td>
                          </tr>
                          <tr v-for="(item, index) in billItems" :key="item.id">
                            <td>{{ index + 1 }}</td>
                            <td>
                              <strong>{{ item.feeDescription }}</strong>
                              <div class="text-muted small">{{ item.incomeTypeDescription }}</div>
                            </td>
                            <td class="text-end">
                              <strong>{{ item.currency || 'KES' }} {{ formatCurrency(item.totalAmount) }}</strong>
                              <div class="text-muted small">{{ item.quantity }} × {{ formatCurrency(item.unitAmount) }}</div>
                            </td>
                            <td class="text-center">
                              <button
                                  class="btn btn-sm btn-outline-danger"
                                  title="Remove"
                                  @click="removeBillItem(item.id)"
                              >
                                <i class="bx bx-trash"></i>
                              </button>
                            </td>
                          </tr>
                          </tbody>
                          <tfoot v-if="billItems.length > 0" class="table-light">
                          <tr class="fw-bold">
                            <td colspan="2" class="text-end">Total:</td>
                            <td class="text-end">KES {{ formatCurrency(calculateTotalAmount()) }}</td>
                            <td></td>
                          </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                    <div class="card-footer" v-if="billItems.length > 0">
                      <button
                          class="btn btn-success w-100 d-none"
                          @click="generateBill"
                          :disabled="billItems.length === 0"
                      >
                        <i class="bx bx-check-circle me-1"></i> Generate Final Bill
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer with action buttons -->
        <div class="modal-footer">
          <div class="container-fluid p-0">
            <div class="row">
              <!-- Left side - action buttons (conditionally shown) -->
              <div class="col" v-if="application.status === 'pending'">
                <div class="d-flex">
                  <button
                      type="button"
                      class="btn btn-success me-2"
                      @click="approveApplication(application.paymentStatus)"
                      :disabled="application.paymentStatus === 'UNPAID'"
                  >
                    <i class="bx bx-check me-1"></i> Approve
                  </button>
                  <button
                      type="button"
                      class="btn btn-danger me-2"
                      @click="declineApplication"
                  >
                    <i class="bx bx-x me-1"></i> Decline
                  </button>
                  <button
                      type="button"
                      class="btn btn-primary me-2"
                      @click="printBill(application.billNo)"
                  >
                    <i class="bx bx-printer me-1"></i> Invoice
                  </button>
                  <button
                      v-if="application.paymentStatus === 'PAID'"
                      type="button"
                      class="btn btn-info me-2"
                      @click="downloadReceipt"
                  >
                    <i class="bx bx-receipt me-1"></i> Receipt
                  </button>
                </div>

                <!-- Payment status warning -->
                <div v-if="application.paymentStatus === 'UNPAID'" class="text-danger small mt-2">
                  <i class="bx bx-info-circle"></i> This application must be paid before approval.
                </div>
              </div>

              <!-- Right side - close button (always shown) -->
              <div class="col-auto">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                  <i class="bx bx-x me-1"></i> Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {execute, econstruction, biller} from "@/api";
export default {
  name: "ApplicationDetailsModal",
  props: {
    applicationId: {
      type: [String, Number],
      required: true
    },
    canApprove: {
      type: Boolean,
      default: false
    },
    canDecline: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      application: {},
      documents: [],
      fees: [],
      payments: [],

      finalFees: [],
      finalPayments: [],
      workflow: [],

      // Billing related data
      incomeTypes: [],
      feesAndCharges: [],
      loadingIncomeTypes: false,
      loadingFeesAndCharges: false,
      selectedIncomeType: null,
      selectedFeeAndCharge: null,
      billItems: [],
      billItem: [],
      billingForm: {
        quantity: 1,
        amount: 0,
        details: ''
      },

      generatedBillNo: null,
      applicationNumber: null

    };
  },
  watch:{
    selectedIncomeType(newVal) {
      if (newVal) {
        this.loadFeesAndCharges(newVal);
      } else {
        this.feesAndCharges = [];
        this.selectedFeeAndCharge = null;
      }
    },

    // Watch for changes to selected fee and charge
    selectedFeeAndCharge2(newVal) {
      if (newVal) {
        // Find the fee and charge object
        const fee = this.feesAndCharges.find(fee => fee.feeId === parseInt(newVal));
        if (fee) {
          // Update the amount field
          this.billingForm.amount = fee.unitFeeAmount;

          // Update the details field with the fee description
          this.billingForm.details = fee.feeDescription;
        }
      } else {
        this.billingForm.amount = 0;
        this.billingForm.details = '';
      }
    },

    selectedFeeAndCharge(newVal) {
      if (newVal) {
        // Find the fee and charge object - try both direct comparison and parseInt
        const fee = this.feesAndCharges.find(fee =>
            fee.feeId === newVal ||
            fee.feeId === String(newVal) ||
            parseInt(fee.feeId) === parseInt(newVal)
        );

        if (fee) {
          // Update the amount field
          this.billingForm.amount = fee.unitFeeAmount;

          // Update the details field with the fee description
          this.billingForm.details = fee.feeDescription;
        }
      } else {
        this.billingForm.amount = 0;
        this.billingForm.details = '';
      }
    }
  },
  mounted() {
    this.fetchApplicationDetails();
    this.$nextTick(() => {
      if (window.$) {
        window.$(`#details-${this.applicationId}`).on('shown.bs.modal', () => {
          console.log('Modal shown event triggered');
        });
      }
    });
  },
  methods: {
    fetchApplicationDetails() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getApplicationDetails");
      data.append("applicationId", this.applicationId);

      // Using your existing API method from the code snippet
      execute(data, econstruction)
          .then((res) => {
            if (res.data.success && res.data.data) {
              const responseData = res.data.data;
              this.application = responseData.application;
              this.documents = responseData.documents || [];
              this.fees = responseData.fees || [];
              this.payments = responseData.payments || [];

              this.finalFees = responseData.finalFees || [];
              this.finalPayments = responseData.finalPayments || [];
              this.workflow = responseData.workflow || [];
              this.loadIncomeTypes()
            } else {
              console.error("Failed to load application details");
              if (this.$toast) {
                this.$toast.error("Failed to load application details");
              }
            }
            this.loading = false;
          })
          .catch((e) => {
            this.loading = false;
            console.error("Error loading application details:", e);
            if (this.$toast) {
              this.$toast.error("Error loading application details: " + (e.message || "Unknown error"));
            }
          });
    },

    capitalizeFirstLetter(string) {
      if (!string) return '';
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';
      const date = new Date(dateTimeString);
      return date.toLocaleString();
    },

    formatCurrency(value) {
      if (!value) return '0.00';
      return parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    getPaymentStatusClass(status) {
      if (!status) return 'badge badge-secondary';

      switch(status.toUpperCase()) {
        case 'PAID':
          return 'badge badge-success';
        case 'PARTIAL':
          return 'badge badge-warning';
        case 'UNPAID':
          return 'badge badge-danger';
        default:
          return 'badge badge-secondary';
      }
    },

    getStatusClass(status) {
      if (!status) return 'badge badge-secondary';

      switch(status.toLowerCase()) {
        case 'approved':
          return 'badge badge-success';
        case 'pending':
          return 'badge badge-warning';
        case 'rejected':
        case 'declined':
          return 'badge badge-danger';
        default:
          return 'badge badge-secondary';
      }
    },

    getWorkflowStatusClass(status) {
      return this.getStatusClass(status);
    },

    formatDocumentType(type) {
      if (!type) return 'Document';

      // Map document types to more readable names
      const typeMap = {
        'landOwnership': 'Land Ownership Document',
        'identificationCard': 'ID Card',
        'kraPin': 'KRA PIN Certificate',
        'architecturalDrawings': 'Architectural Drawings',
        'structuralDrawings': 'Structural Drawings',
        'sitePlan': 'Site Plan'
      };

      return typeMap[type] || this.capitalizeFirstLetter(type.replace(/([A-Z])/g, ' $1').trim());
    },

    formatProfessionalType(type) {
      if (!type) return 'N/A';

      const typeMap = {
        'architect': 'Architect',
        'civilEngineer': 'Civil Engineer',
        'structuralEngineer': 'Structural Engineer',
        'mechanicalEngineer': 'Mechanical Engineer',
        'electricalEngineer': 'Electrical Engineer',
        'quantitySurveyor': 'Quantity Surveyor',
        'planner': 'Planner'
      };

      return typeMap[type] || this.capitalizeFirstLetter(type.replace(/([A-Z])/g, ' $1').trim());
    },

    formatWorkflowStage(stage) {
      if (!stage) return 'N/A';

      const stageMap = {
        'submission': 'Application Submission',
        'payment': 'Payment Confirmation',
        'review': 'Technical Review',
        'approval': 'Final Approval',
        'rejection': 'Application Rejection'
      };

      return stageMap[stage] || this.capitalizeFirstLetter(stage);
    },

    getSubCountyName() {
      if (this.payments && this.payments.length > 0 && this.payments[0].subCountyName) {
        return this.payments[0].subCountyName;
      }
      return 'N/A';
    },

    getWardName() {
      if (this.payments && this.payments.length > 0 && this.payments[0].wardName) {
        return this.payments[0].wardName;
      }
      return 'N/A';
    },

    getTotalAmount() {
      const total = this.payments.reduce((sum, payment) => sum + parseFloat(payment.detailAmount || 0), 0);
      return this.formatCurrency(total);
    },

    getTotalPaid() {
      const total = this.payments.reduce((sum, payment) => sum + parseFloat(payment.receiptAmount || 0), 0);
      return this.formatCurrency(total);
    },

    getTotalBalance() {
      const total = this.payments.reduce((sum, payment) => sum + parseFloat(payment.billBalance || 0), 0);
      return this.formatCurrency(total);
    },

    approveApplication(paymentStatus) {

      if(paymentStatus ==="UNPAID"){
        alert("The application is UNPAID. It needs to be paid before approving")
        return
      }


      this.generateBill();

      //this.$emit('approve', this.application);
    },

    declineApplication() {
      this.$emit('decline', this.application);
    },

    downloadInvoice() {
      this.$emit('download-invoice', this.application);
    },

    downloadReceipt() {
      this.$emit('download-receipt', this.application);
    },

    // Load income types from API
    loadIncomeTypes() {
      this.loadingIncomeTypes = true;

      const data = new FormData();
      data.append("function", "getIncomeTypes");
      data.append("incomeTypePrefix", "PLANS");

      execute(data, biller)
          .then((res) => {
            this.loadingIncomeTypes = false;

            if (res.data.success && res.data.data && res.data.data.incomeTypes) {
              this.incomeTypes = res.data.data.incomeTypes;
            } else {
              console.error("Failed to load income types");
              if (this.$toast) {
                this.$toast.error("Failed to load income types");
              }
            }
          })
          .catch((e) => {
            this.loadingIncomeTypes = false;
            console.error("Error loading income types:", e);
            if (this.$toast) {
              this.$toast.error("Error loading income types: " + (e.message || "Unknown error"));
            }
          });
    },

    // Load fees and charges based on selected income type
    loadFeesAndCharges(incomeTypeId) {
      this.loadingFeesAndCharges = true;
      this.feesAndCharges = [];
      this.selectedFeeAndCharge = null;

      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller)
          .then((res) => {
            this.loadingFeesAndCharges = false;

            if (res.data.success && res.data.data && res.data.data.feesAndCharges) {
              this.feesAndCharges = res.data.data.feesAndCharges;
            } else {
              console.error("Failed to load fees and charges");
              if (this.$toast) {
                this.$toast.error("Failed to load fees and charges");
              }
            }
          })
          .catch((e) => {
            this.loadingFeesAndCharges = false;
            console.error("Error loading fees and charges:", e);
            if (this.$toast) {
              this.$toast.error("Error loading fees and charges: " + (e.message || "Unknown error"));
            }
          });
    },

    // Add item to bill
    addBillItem2() {
      if (!this.selectedIncomeType || !this.selectedFeeAndCharge) {
        if (this.$toast) {
          this.$toast.error("Please select an income type and fee");
        }
        return;
      }

      // Find the income type and fee objects
      const incomeType = this.incomeTypes.find(type => type.incomeTypeId === parseInt(this.selectedIncomeType));
      const fee = this.feesAndCharges.find(fee => fee.feeId === parseInt(this.selectedFeeAndCharge));

      if (!incomeType || !fee) {
        if (this.$toast) {
          this.$toast.error("Selected income type or fee not found");
        }
        return;
      }

      // Calculate total amount
      const totalAmount = parseFloat(this.billingForm.quantity) * parseFloat(fee.unitFeeAmount);

      // Create bill item
      const billItem = {
        id: Date.now(), // Unique ID for the item
        incomeTypeId: incomeType.incomeTypeId,
        incomeTypeDescription: incomeType.incomeTypeDescription,
        feeId: fee.feeId,
        feeDescription: fee.feeDescription,
        quantity: this.billingForm.quantity,
        unitAmount: fee.unitFeeAmount,
        totalAmount: totalAmount,
        details: this.billingForm.details
      };

      // Add to bill items
      this.billItems.push(billItem);

      // Reset form
      this.resetBillingForm();

      // Show success message
      if (this.$toast) {
        this.$toast.success("Item added to bill");
      }
    },
    addBillItem() {
      if (!this.selectedIncomeType || !this.selectedFeeAndCharge) {
        if (this.$toast) {
          this.$toast.error("Please select an income type and fee");
        }
        return;
      }

      // Find the income type and fee objects with flexible type comparison
      const incomeType = this.incomeTypes.find(type =>
          String(type.incomeTypeId) === String(this.selectedIncomeType)
      );

      const fee = this.feesAndCharges.find(fee =>
          String(fee.feeId) === String(this.selectedFeeAndCharge)
      );

      if (!incomeType || !fee) {
        if (this.$toast) {
          this.$toast.error("Selected income type or fee not found");
        }
        return;
      }

      // Calculate total amount - ensure numeric values
      const quantity = parseFloat(this.billingForm.quantity) || 1; // Default to 1 if NaN
      const unitAmount = parseFloat(fee.unitFeeAmount) || 0; // Default to 0 if NaN
      const totalAmount = quantity * unitAmount;

      // Create bill item
      const billItem = {
        id: Date.now(), // Unique ID for the item
        incomeTypeId: incomeType.incomeTypeId,
        incomeTypeDescription: incomeType.incomeTypeDescription,
        feeId: fee.feeId,
        feeDescription: fee.feeDescription,
        quantity: quantity,
        unitAmount: unitAmount,
        totalAmount: totalAmount,
        details: this.billingForm.details,
        currency: fee.currency || 'KES' // Add currency from fee object
      };

      // Add to bill items
      this.billItems.push(billItem);

      // Reset form
      this.resetBillingForm();

      // Show success message
      if (this.$toast) {
        this.$toast.success("Item added to bill");
      }
    },

    // Remove item from bill
    removeBillItem(itemId) {
      this.billItems = this.billItems.filter(item => item.id !== itemId);
    },

    // Reset billing form
    resetBillingForm() {
      this.selectedFeeAndCharge = null;
      this.billingForm = {
        quantity: 1,
        amount: 0,
        details: ''
      };
    },

    // Calculate total bill amount
    calculateTotalAmount() {
      return this.billItems.reduce((total, item) => total + item.totalAmount, 0);
    },

    // Prepare bill items for submission
    prepareBillItems() {
      this.billItem = []; // Reset the bill item array

      for (const { incomeTypeDescription, feeId, feeDescription, unitAmount, accountNo, costCenterNo, incomeTypeId, quantity, totalAmount } of this.billItems) {
        this.billItem.push({
          fiscalYear: new Date().getFullYear(),
          typeDescription: incomeTypeDescription,
          feeDescription,
          quantity,
          unitFeeAmount: unitAmount,
          accountNo,
          costCenterNo,
          incomeTypeId,
          revenueStreamItem: "",
          feeId,
          amount: totalAmount,
          customer: this.application.plot_number,
          zone: this.application.zone || "",
          subCountyID: this.application.subCountyID,
          subCountyName: this.application.subCountyName,
          wardID: this.application.wardID,
          wardName: this.application.wardName,
          idNo: sessionStorage.getItem("sessionIdNo"),
          phoneNumber: sessionStorage.getItem("sessionPhoneNumber"),
          names: sessionStorage.getItem("sessionNames"),
          customerPhoneNumber: this.application.owner_phone_number,
          description: "",
          customerName: this.application.owner_full_names
        });
      }

      return this.billItem;
    },

    // Generate bill
    generateBill() {
      const billItems = this.prepareBillItems();

      const data = new FormData();
      data.append("function", "generateBill3");
      data.append("billItem", JSON.stringify(billItems));

      return execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              // Store the bill number for reference
              this.generatedBillNo = res.data.data.billGenerated.billNo;

              this.$emit('approve', this.application,res.data.data.billGenerated.billNo);

              // Print the bill
              this.printBill(this.generatedBillNo);

              // Return the bill number for chaining
              return this.generatedBillNo;
            } else {
              throw new Error(res.data.message || "Failed to generate bill");
            }
          });
    },
    getFinalTotalAmount() {
      const total = this.finalFees.reduce((sum, payment) => sum + parseFloat(payment.detailAmount || 0), 0);
      return this.formatCurrency(total);
    },

    getFinalTotalPaid() {
      const total = this.finalFees.reduce((sum, payment) => sum + parseFloat(payment.receiptAmount || 0), 0);
      return this.formatCurrency(total);
    },

    getFinalTotalBalance() {
      const total = this.finalFees.reduce((sum, payment) => sum + parseFloat(payment.billBalance || 0), 0);
      return this.formatCurrency(total);
    },


    printBill(billNo) {
      localStorage['params'] = JSON.stringify({
        billNo: billNo
      });
      const routeData = this.$router.resolve({name: 'bill'});
      window.open(routeData.href, '_blank');
    },
  }
};
</script>

<style scoped>
/* Base styling */
.modal-body {
  padding: 1.5rem;
}

.modal-header {
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
}

.modal-footer {
  border-top: 1px solid #e0e0e0;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
}

/* Status badges */
.status-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  font-weight: 500;
  font-size: 0.875rem;
}

.payment-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
}

/* Status summary card */
.status-summary .card {
  border-left: 4px solid #3498db;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Section styling */
.section-title {
  margin-top: 0;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
  color: #495057;
  font-weight: 600;
}

/* Info cards */
.info-card {
  background-color: #fff;
  border-radius: 0.375rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e0e0e0;
}

/* Tables */
.details-table {
  margin-bottom: 0;
}

.details-table th {
  font-weight: 500;
  color: #6c757d;
  vertical-align: top;
  padding: 0.5rem 0.75rem 0.5rem 0;
}

.details-table td {
  padding: 0.5rem 0;
  border-top: none;
}

/* Better tab styling */
.nav-tabs {
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.nav-tabs .nav-link {
  border: none;
  color: #6c757d;
  padding: 0.75rem 1rem;
  font-weight: 500;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
}

.nav-tabs .nav-link.active {
  color: #3498db;
  border-bottom: 2px solid #3498db;
  background-color: transparent;
}

.nav-tabs .nav-link:hover:not(.active) {
  border-bottom: 2px solid #dee2e6;
  color: #495057;
}

/* Document list */
.document-list {
  max-height: 400px;
  overflow-y: auto;
}

.document-item {
  padding: 0.75rem 0;
}

/* Workflow status pills */
.status-pill {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #f8f9fa;
}

.workflow-stage {
  font-weight: 500;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .tab-content {
    padding: 1rem 0.5rem;
  }

  .info-card {
    padding: 1rem;
  }

  .modal-footer, .modal-header {
    padding: 0.75rem 1rem;
  }
}
</style>